import '../css/Card.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import $ from 'jquery';
import Flag from 'react-world-flags';
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useCard } from '../hooks/fetchCard';
import UnkownPlayer from '../assets/unknownPlayer.png';
import UnkownTeam from '../assets/Unknown.png';
import Logo from '../assets/Logo.png';

const Card = forwardRef(({ cardID, card, parsedPlayer, isOpening, isSlowOpening, showBack, size, market, scale }, ref) => {
    const { fetchCard } = useCard(cardID);
    const [cardData, setCardData] = useState(card || null);
    const SHOWPLAYER = process.env.REACT_APP_SHOW_PLAYERS === 'true';

    // Helper functions that will be used in hooks
    const getTierClass = (tier) => {
        switch (tier) {
            case 1:
                return 'bronzeCard';
            case 2:
                return 'silverCard';
            case 3:
                return 'goldCard';
            case 4:
                return 'legendaryCard';
            default:
                console.error('Invalid tier');
                return '';
        }
    };

    const formatStat = (stat) => {
        return stat < 10 ? `0${stat}` : stat;
    };

    const getCardBackgrounds = () => {
        if (cardData?.background) {
            switch (cardData.background.name) {
                case 'Champion':
                    return {
                        static: `${process.env.PUBLIC_URL}/Champion-static.png`,
                        gif: `${process.env.PUBLIC_URL}/Champion.gif`,
                        isGif: true
                    };
                case 'CardBackground1':
                    return { static: `${process.env.PUBLIC_URL}/cardBackground1.png`, isGif: false };
                case 'CardBackground2':
                    return { static: `${process.env.PUBLIC_URL}/cardBackground2.png`, isGif: false };
                case 'Flower Grid':
                    return { static: `${process.env.PUBLIC_URL}/FlowerGrid.png`, isGif: false };
                case 'Interlocked':
                    return { static: `${process.env.PUBLIC_URL}/Interlocked.png`, isGif: false };
                case 'Convergence':
                    return { static: `${process.env.PUBLIC_URL}/Convergence.png`, isGif: false };
                case 'Arsenal':
                    return { static: `${process.env.PUBLIC_URL}/Arsenal.png`, isGif: false };
                case 'Default':
                    return { static: `${process.env.PUBLIC_URL}/defaultCardBackground.png`, isGif: false };
                default:
                    console.error('Invalid background:', cardData.background.name);
                    return { static: `${process.env.PUBLIC_URL}/defaultCardBackground.png`, isGif: false };
            }
        } else if (parsedPlayer?.player?.background) {
            switch (parsedPlayer.player.background.name) {
                case 'Champion':
                    return {
                        static: `${process.env.PUBLIC_URL}/Champion-static.png`,
                        gif: `${process.env.PUBLIC_URL}/Champion.gif`,
                        isGif: true
                    };
                case 'CardBackground1':
                    return { static: `${process.env.PUBLIC_URL}/cardBackground1.png`, isGif: false };
                case 'CardBackground2':
                    return { static: `${process.env.PUBLIC_URL}/cardBackground2.png`, isGif: false };
                case 'Flower Grid':
                    return { static: `${process.env.PUBLIC_URL}/FlowerGrid.png`, isGif: false };
                case 'Interlocked':
                    return { static: `${process.env.PUBLIC_URL}/Interlocked.png`, isGif: false };
                case 'Convergence':
                    return { static: `${process.env.PUBLIC_URL}/Convergence.png`, isGif: false };
                case 'Arsenal':
                    return { static: `${process.env.PUBLIC_URL}/Arsenal.png`, isGif: false };
                case 'Default':
                    return { static: `${process.env.PUBLIC_URL}/defaultCardBackground.png`, isGif: false };
            }
        } else {
            return { static: `${process.env.PUBLIC_URL}/defaultCardBackground.png`, isGif: false };
        }
    };

    const getCardOutlineEffect = () => {
        if (cardData && cardData.outline && cardData.outline.name) {
            switch (cardData.outline.name) {
                case 'Fire':
                    return "1";
                case 'Electric':
                    return "2";
                case 'Mystic':
                    return "3";
                case 'Red':
                    return "4";
                case 'Blue':
                    return "5";
                case 'Green':
                    return "6";
                default:
                    return "0";
            }
        } else if (parsedPlayer?.player?.outline) {
            switch (parsedPlayer.player.outline.name) {
                case 'Fire':
                    return "1";
                case 'Electric':
                    return "2";
                case 'Mystic':
                    return "3";
                case 'Red':
                    return "4";
                case 'Blue':
                    return "5";
                case 'Green':
                    return "6";
                default:
                    return "0";
            }
        }
        return "0";
    };

    // Calculate derived values directly (no memoization at card level since inventory handles caching)
    const cardBackgrounds = getCardBackgrounds();
    const cardOutlineEffect = getCardOutlineEffect();
    const player = parsedPlayer && !cardData ? parsedPlayer.player : cardData?.player;
    
    const tierClass = (() => {
        if (parsedPlayer && !cardData) {
            return getTierClass(parsedPlayer.tier);
        } else if (cardData) {
            return getTierClass(cardData.tier);
        }
        return '';
    })();
    
    const teamImageSrc = (() => {
        const parsedImage = parsedPlayer?.player?.team?.image;
        const cardImage = cardData?.player?.team?.image;
        
        if (parsedImage && !cardData && !parsedImage.startsWith('/dynamic-svg/teamplaceholder')) {
            return parsedImage;
        }
        
        if (cardImage && !cardImage.startsWith('/dynamic-svg/teamplaceholder')) {
            return cardImage;
        }
        
        return UnkownTeam;
    })();

    const playerImageSrc = (() => {
        if (SHOWPLAYER === true) {
            if (parsedPlayer && !cardData) {
                return parsedPlayer.player.image;
            }
            return cardData?.player?.image ? cardData.player.image : cardData?.image;
        }
        return UnkownPlayer;
    })();

    useEffect(() => {
        if (!card && cardID) {
            fetchCard(cardID).then((data) => {
                setCardData(data);
            });
        } else if (card) {
            setCardData(card);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardID, card]);

    useImperativeHandle(ref, () => ({
        openCard
    }));

    const openCard = (e) => {
        if (e) {
            e.preventDefault();
            $(e.currentTarget).addClass('active');
        } else if (cardID) {
            $(`.card-${cardID}`).addClass('active');
        } else if (parsedPlayer) {
            $(`.card-${parsedPlayer.player.id}`).addClass('active');
        } else if (cardData) {
            $(`.card-${cardData.id}`).addClass('active');
        } else {
            $(`.card-undefined`).addClass('active');
        }
        // Ensure the card is scrolled into view
        const cardElement = document.querySelector(`.card-${cardID}`);
        if (cardElement) {
            cardElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
    };

    if (!cardData) {
        if (!parsedPlayer) {
            return null;
        }
    }

    const getBoostTierColor = (boost) => {
        switch (boost.tier) {
            case 1:
                return 'var(--bronze1)';
            case 2:
                return 'var(--silver1)';
            case 3:
                return 'var(--gold1)';
            default:
                console.error('Invalid boost tier');
                return '';
        }
    };

    return (
        <div className={`card-container ${size === 'small' ? 'small' : ''}`} style={{ transform: `scale(${scale})`, position: 'relative' }}>
            <div
                className={`card-${cardID} ${isOpening ? `${isSlowOpening ? "slow-opening" : "opening"}` : "active static"} ${market ? "market" : ""} ${tierClass}`}
                id="card"
                onMouseEnter={e => {
                    const staticImg = e.currentTarget.querySelector('.card-bg-static');
                    const gifImg = e.currentTarget.querySelector('.card-bg-gif');
                    if (gifImg && staticImg) {
                        gifImg.style.opacity = 1;
                        gifImg.style.pointerEvents = 'auto';
                        staticImg.style.opacity = 0;
                    }
                }}
                onMouseLeave={e => {
                    const staticImg = e.currentTarget.querySelector('.card-bg-static');
                    const gifImg = e.currentTarget.querySelector('.card-bg-gif');
                    if (gifImg && staticImg) {
                        gifImg.style.opacity = 0;
                        gifImg.style.pointerEvents = 'none';
                        staticImg.style.opacity = 1;
                    }
                }}
                style={{ position: 'relative' }}
            >
                <svg className="card-effect-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1" preserveAspectRatio="none" data-effect={cardOutlineEffect}>
                    <defs>
                        <clipPath id="svgPath" clipPathUnits="objectBoundingBox">
                            <path fill="#000" d="M 0.993 0.126 C 0.966 0.126 0.943 0.121 0.924 0.114 C 0.894 0.101 0.874 0.083 0.872 0.061 C 0.872 0.058 0.871 0.054 0.871 0.052 C 0.871 0.049 0.866 0.049 0.863 0.047 C 0.826 0.036 0.787 0.027 0.749 0.021 C 0.67 0.007 0.584 0 0.5 0 C 0.415 0 0.332 0.007 0.251 0.021 C 0.21 0.027 0.174 0.037 0.137 0.047 C 0.136 0.049 0.131 0.049 0.131 0.052 C 0.131 0.054 0.128 0.058 0.128 0.061 C 0.126 0.083 0.106 0.101 0.076 0.114 C 0.055 0.121 0.031 0.126 0.01 0.126 C 0.002 0.127 0.001 0.127 0.001 0.131 L 0.001 0.818 C 0.001 0.825 0.001 0.835 0.002 0.842 C 0.006 0.855 0.015 0.866 0.032 0.876 C 0.045 0.884 0.064 0.888 0.08 0.891 C 0.128 0.901 0.174 0.908 0.221 0.918 C 0.284 0.931 0.345 0.946 0.405 0.963 C 0.44 0.972 0.472 0.985 0.5 0.999 L 0.5 1 L 0.5 0.999 C 0.525 0.983 0.559 0.973 0.59 0.963 C 0.654 0.946 0.717 0.931 0.777 0.918 L 0.917 0.891 C 0.936 0.888 0.948 0.884 0.966 0.876 C 0.987 0.866 0.994 0.855 0.998 0.842 C 0.999 0.835 0.999 0.825 0.999 0.818 L 0.999 0.542 L 0.999 0.143 L 0.999 0.131 C 0.999 0.127 0.998 0.127 0.993 0.126 Z" />
                        </clipPath>

                        {/* Fire gradient for classic fire effect */}
                        <linearGradient id="fireGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style={{ stopColor: '#ff4500', stopOpacity: 0.8 }} />
                            <stop offset="25%" style={{ stopColor: '#ff6b35', stopOpacity: 1 }} />
                            <stop offset="50%" style={{ stopColor: '#ff8c42', stopOpacity: 1 }} />
                            <stop offset="75%" style={{ stopColor: '#ffa500', stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: '#ff4500', stopOpacity: 0.8 }} />
                        </linearGradient>

                        {/* Electric gradient for lightning effect */}
                        <linearGradient id="electricGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style={{ stopColor: '#00ffff', stopOpacity: 0.9 }} />
                            <stop offset="25%" style={{ stopColor: '#87ceeb', stopOpacity: 1 }} />
                            <stop offset="50%" style={{ stopColor: '#ffffff', stopOpacity: 1 }} />
                            <stop offset="75%" style={{ stopColor: '#00bfff', stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: '#00ffff', stopOpacity: 0.9 }} />
                        </linearGradient>

                        {/* Green gradient for mystic green effect */}
                        <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style={{ stopColor: '#00ff00', stopOpacity: 0.8 }} />
                            <stop offset="25%" style={{ stopColor: '#32cd32', stopOpacity: 1 }} />
                            <stop offset="50%" style={{ stopColor: '#7fff00', stopOpacity: 1 }} />
                            <stop offset="75%" style={{ stopColor: '#90ee90', stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: '#00ff00', stopOpacity: 0.8 }} />
                        </linearGradient>

                        <filter id="effectGlow">
                            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    <path
                        className="effect-path"
                        fill="none"
                        strokeWidth="0.008"
                        filter="url(#effectGlow)"
                        d="M 0.993 0.126 C 0.966 0.126 0.943 0.121 0.924 0.114 C 0.894 0.101 0.874 0.083 0.872 0.061 C 0.872 0.058 0.871 0.054 0.871 0.052 C 0.871 0.049 0.866 0.049 0.863 0.047 C 0.826 0.036 0.787 0.027 0.749 0.021 C 0.67 0.007 0.584 0 0.5 0 C 0.415 0 0.332 0.007 0.251 0.021 C 0.21 0.027 0.174 0.037 0.137 0.047 C 0.136 0.049 0.131 0.049 0.131 0.052 C 0.131 0.054 0.128 0.058 0.128 0.061 C 0.126 0.083 0.106 0.101 0.076 0.114 C 0.055 0.121 0.031 0.126 0.01 0.126 C 0.002 0.127 0.001 0.127 0.001 0.131 L 0.001 0.818 C 0.001 0.825 0.001 0.835 0.002 0.842 C 0.006 0.855 0.015 0.866 0.032 0.876 C 0.045 0.884 0.064 0.888 0.08 0.891 C 0.128 0.901 0.174 0.908 0.221 0.918 C 0.284 0.931 0.345 0.946 0.405 0.963 C 0.44 0.972 0.472 0.985 0.5 0.999 L 0.5 1 L 0.5 0.999 C 0.525 0.983 0.559 0.973 0.59 0.963 C 0.654 0.946 0.717 0.931 0.777 0.918 L 0.917 0.891 C 0.936 0.888 0.948 0.884 0.966 0.876 C 0.987 0.866 0.994 0.855 0.998 0.842 C 0.999 0.835 0.999 0.825 0.999 0.818 L 0.999 0.542 L 0.999 0.143 L 0.999 0.131 C 0.999 0.127 0.998 0.127 0.993 0.126 Z"
                    />
                </svg>
                <div id="card-inner" className={`${showBack ? 'showback-mode' : ''}`}>
                    <div id="card-top">
                        {!showBack ? (
                            <>
                                <div className="info">
                                    <div className="value">{formatStat(player?.stats?.[0]?.firepower)}</div>
                                    <div className="position">{cardData && cardData.role}</div>
                                    <div className="country"><div><Flag className='flag-card' code={parsedPlayer && !cardData ? parsedPlayer.player.country.code : cardData.player.country.code} /></div></div>
                                    <div className="club"><img src={teamImageSrc} style={{ transform: 'scale(0.8)' }} alt='team logo' loading='lazy' ></img></div>
                                </div>
                                <div className="image">
                                    <img src={playerImageSrc} style={{ width: '100%', transform: 'scale(1.2)' }} alt="player" />
                                </div>
                                <div className="backfont">CS</div>
                                {/* Card background image: if GIF, swap on hover; otherwise, just show static */}
                                {(() => {
                                    if (cardBackgrounds.isGif) {
                                        return (
                                            <>
                                                <img
                                                    className="card-bg-static"
                                                    src={cardBackgrounds.static}
                                                    style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, transition: 'opacity 0.2s' }}
                                                    alt=""
                                                />
                                                <img
                                                    className="card-bg-gif"
                                                    src={cardBackgrounds.gif}
                                                    style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, opacity: 0, pointerEvents: 'none', transition: 'opacity 0.2s' }}
                                                    alt=""
                                                />
                                            </>
                                        );
                                    } else {
                                        return (
                                            <img
                                                src={cardBackgrounds.static}
                                                style={{ width: '100%', height: '100%', transform: 'scale(1.6)', position: 'absolute', top: 0, left: 0 }}
                                                alt=""
                                            />
                                        );
                                    }
                                })()}
                            </>
                        ) : (
                            <>
                                {(() => {
                                    if (cardBackgrounds.isGif) {
                                        return (
                                            <>
                                                <img
                                                    className="card-bg-static"
                                                    src={cardBackgrounds.static}
                                                    style={{ width: '100%', height: '100%', position: 'absolute', top: '6.5vw', left: 0, transform: 'scale(2.3)', transition: 'opacity 0.2s' }}
                                                    alt=""
                                                />
                                                <img
                                                    className="card-bg-gif"
                                                    src={cardBackgrounds.gif}
                                                    style={{ width: '100%', height: '100%', position: 'absolute', top: '6.5vw', left: 0, transform: 'scale(2.3)', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.2s' }}
                                                    alt=""
                                                />
                                            </>
                                        );
                                    } else {
                                        return (
                                            <img
                                                src={cardBackgrounds.static}
                                                style={{ width: '100%', height: '100%', position: 'absolute', top: '6.5vw', left: 0, transform: 'scale(2.3)', transition: 'opacity 0.2s' }}
                                                alt=""
                                            />
                                        );
                                    }
                                })()}
                                <img src={Logo} alt="Logo" id="card-logo-back" />
                            </>
                        )}
                    </div>
                    <div id="card-bottom">
                        {!showBack && (
                            <>
                                <div className="name">
                                    {parsedPlayer && !cardData ? parsedPlayer.player.name : cardData.player.name}
                                </div>
                                <div className="stats">
                                    <div>
                                        <ul>
                                            <li><span>{formatStat(player?.stats?.[0]?.entrying)}</span><span className='statText'>entry</span></li>
                                            <li><span>{formatStat(player?.stats?.[0]?.opening)}</span><span className='statText'>open</span></li>
                                            <li><span>{formatStat(player?.stats?.[0]?.sniping)}</span><span className='statText'>awp</span></li>
                                        </ul>
                                    </div>
                                    <div>
                                        <ul>
                                            <li><span>{formatStat(player?.stats?.[0]?.trading)}</span><span className='statText'>trade</span></li>
                                            <li><span>{formatStat(player?.stats?.[0]?.clutching)}</span><span className='statText'>clutch</span></li>
                                            <li><span>{formatStat(player?.stats?.[0]?.utility)}</span><span className='statText'>utility</span></li>
                                        </ul>
                                    </div>
                                </div>
                                <div className={`${cardData?.boosts && "card-info"}`}>
                                    {cardData?.boosts && cardData.boosts.map((boost, index) => (
                                        <div key={index} className="boost-item">
                                            <div className="boost-name-container">
                                                <div className="svg-container">
                                                    <svg width="0.5vw" height="0.5vw" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="0.2vw" cy="0.2vw" r="0.2vw" fill={getBoostTierColor(boost)} />
                                                    </svg>
                                                </div>
                                                <span className="boost-name">
                                                    {boost.name}:
                                                </span>
                                            </div>
                                            <span className="boost-value">
                                                +{boost.value} pts
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Card;